import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, Provider, inject } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { DropdownSingleComponent } from "rl-common/components/dropdown/dropdown-single/dropdown-single.component";
import { DropdownOptions } from "rl-common/components/dropdown/dropdown.models";
import { FormTableControlDirective } from "rl-common/components/form-table/form-table-control.directive";
import { FormTableRowComponent } from "rl-common/components/form-table/form-table-row/form-table-row.component";
import { TextInputComponent } from "rl-common/components/text/text-input/text-input.component";
import { IAccountingProcessAccount } from "rl-common/services/accounting-processes/models/i-accounting-process-account";
import { IAccountingProcessAccountGroup } from "rl-common/services/accounting-processes/models/i-accounting-process-account-group";
import { IAccountingProcessAccountGroupRuleSet } from "rl-common/services/accounting-processes/models/i-accounting-process-account-group-rule-set";
import { ICreateAccountingProcessAccountGroupRuleSet } from "rl-common/services/accounting-processes/models/i-create-accounting-process-account-group-rule-set";
import { BuildAccountGroupRuleSetModalAdapter, IBuildAccountGroupRuleSetModalComponent } from "./build-account-group-rule-set-modal.adapter";
import { IBuildAccountGroupRuleSetResult } from "./build-account-group-rule-set-modal.models";

export interface IAccountGroupRuleSetForm {
	name: FormControl<string>;
	account: FormControl<IAccountingProcessAccount>;
}

@Component({
	selector: "rl-build-account-group-rule-set-modal",
	templateUrl: "./build-account-group-rule-set-modal.component.html",
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [FormTableRowComponent, FormTableControlDirective, DropdownSingleComponent, TextInputComponent, ReactiveFormsModule]
})
export class BuildAccountGroupRuleSetModalComponent implements OnInit, IBuildAccountGroupRuleSetModalComponent {
	private readonly _fb = inject(FormBuilder);


	@Input()
	accounts: IAccountingProcessAccount[];

	@Input()
	accountGroup: IAccountingProcessAccountGroup;

	@Input()
	accountGroupRuleSet: IAccountingProcessAccountGroupRuleSet;

	@Input()
	maxSequenceNumber: number;

	@Input()
	existingNames: string[] = [];

	@Output()
	onClose = new EventEmitter<IBuildAccountGroupRuleSetResult>();

	isNewAccountGroupRuleSet: boolean;
	pageTitle: string;

	form: FormGroup<IAccountGroupRuleSetForm>;

	accountItems: IAccountingProcessAccount[] = [];
	accountOptions: DropdownOptions<IAccountingProcessAccount> = {
		items: this.accountItems,
		rowKeyFn: (account: IAccountingProcessAccount) => account.id,
		rowLabelFn: (account: IAccountingProcessAccount) => account.name
	};

	ngOnInit(): void {
		this.isNewAccountGroupRuleSet = !this.accountGroupRuleSet?.groupRuleSetId;

		this.pageTitle = this.isNewAccountGroupRuleSet
			? `Add New Rule Set to Account Group: <strong>${this.accountGroup.groupName}</strong>`
			: `Edit Rule Set on Account Group: <strong>${this.accountGroup.groupName}</strong>`;

		this.accountItems = [...this.accounts].sort((a, b) => a.name.localeCompare(b.name));
		this.accountOptions.items = this.accountItems;

		// on edit, exclude this Set's own name so re-saving it unchanged is not a "duplicate"
		const takenNames = this.existingNames.filter(n => n?.toLowerCase() !== this.accountGroupRuleSet?.name?.toLowerCase());

		this.form = this._fb.group<IAccountGroupRuleSetForm>({
			name: this._fb.control(this.accountGroupRuleSet?.name ?? "", [Validators.required, duplicateRuleSetNameValidator(takenNames)]),
			account: this._fb.control(this.accountItems.find(a => a.id === this.accountGroupRuleSet?.accountId) ?? null, { validators: Validators.required })
		});
	}

	save() {
		if (!this.form.valid) {
			return;
		}

		const accountGroupRuleSet: ICreateAccountingProcessAccountGroupRuleSet = {
			groupRuleSetId: this.accountGroupRuleSet?.groupRuleSetId ?? null,
			groupId: this.accountGroup.id,
			// new Sets append to the end of the evaluation order; an edit keeps its place
			sequenceNumber: this.isNewAccountGroupRuleSet ? this.maxSequenceNumber + 1 : this.accountGroupRuleSet.sequenceNumber,
			name: this.form.controls.name.value.trim(),
			accountId: this.form.controls.account.value.id
		};

		this.onClose.next({ accountGroupRuleSet });
	}

	close() {
		this.onClose.next(null);
	}
}

/** Set names are how users tell Sets apart in the grouped list, so keep them unique per group. */
export function duplicateRuleSetNameValidator(takenNames: string[]): ValidatorFn {
	return (control: FormControl<string>): ValidationErrors | null => {
		const name = control.value?.trim().toLowerCase();
		if (!name) {
			return null;
		}
		return takenNames.some(n => n?.trim().toLowerCase() === name) ? { duplicateName: true } : null;
	};
}

export const BUILDACCOUNTGROUPRULESET_MODAL_PROVIDER: Provider = {
	provide: BuildAccountGroupRuleSetModalAdapter,
	useFactory: () => new BuildAccountGroupRuleSetModalAdapter(BuildAccountGroupRuleSetModalComponent)
};
